import _ from 'lodash';
import {
  prepareMultiplayerState,
} from 'actions/gameActions';

import {
  SHOW_FEEDBACK,
  CLOSE_FEEDBACK,
  SHOW_STATS,
  SET_CURRENT_ACTIVITY,
  OPEN_ACTIVITY_HELP,
  CLOSE_ACTIVITY_HELP,
  GAME_INITIALIZED,
  RESTART_ACTIVITY,
  SHOW_GAME_PARENT_ADVICE,
  SHOW_GAME_MULTIPLAYER_ADVICE,
  OPEN_TEAM_ACTIVITY_HELP,
  CLOSE_TEAM_ACTIVITY_HELP,
  ADD_IDLE_TIME,
  CHANGE_PLAYER_TURN,
  INCREMENT_PLAYER_POINTS,
  SET_PLAYER_POWER_LEVEL,
} from 'actions/types';

const EMPTY_ASSESSMENT_FEEDBACK_STATS = {
  correctAnswers: 0,
  totalQuestions: 0,
  currentHadIncorrect: false,
  answersByQuestion: [],
};

const INITIAL_STATE = {
  isPreview: false,
  goBackHandler: null,
  currentActivity: null,
  shouldGameInit: false,
  initialTeamActivityHelpShown: false,
  isTeamActivityHelpOpened: false,
  initialActivityHelpShown: false,
  isActivityHelpOpened: false,
  stats: null,
  gameParentAdvice: false,
  gameMultiplayerAdvice: false,
  numberOfPlays: 0,
  gameInitialized: false,
  feedback: null,
  idleTime: 0,
  players: [],
  multiplayerModeEnabled: false,
  turningPlayerId: null,
  points: {},
  powerLevels: {},
  assessmentFeedbackStats: EMPTY_ASSESSMENT_FEEDBACK_STATS,
};

const filter = 'game';

const toFiniteNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const updateAssessmentFeedbackStats = (stats = EMPTY_ASSESSMENT_FEEDBACK_STATS, feedback = {}) => {
  if (typeof feedback?.isCorrect !== 'boolean') {
    return stats || EMPTY_ASSESSMENT_FEEDBACK_STATS;
  }

  const current = stats || EMPTY_ASSESSMENT_FEEDBACK_STATS;

  if (!feedback.isCorrect) {
    return {
      ...current,
      currentHadIncorrect: true,
    };
  }

  const answersByQuestion = Array.isArray(current.answersByQuestion)
    ? current.answersByQuestion.slice()
    : [];
  const currentAnswer = current.currentHadIncorrect ? 0 : 1;
  answersByQuestion.push(currentAnswer);

  const nextTotal = answersByQuestion.length;
  const nextCorrect = answersByQuestion.reduce((sum, value) => sum + Number(value || 0), 0);

  return {
    correctAnswers: nextCorrect,
    totalQuestions: nextTotal,
    currentHadIncorrect: false,
    answersByQuestion,
  };
};

const normalizeStatsWithAssessmentFeedback = (payload = {}, feedbackStats = EMPTY_ASSESSMENT_FEEDBACK_STATS) => {
  if (!payload?.withScore) {
    return payload;
  }

  const data = payload.data || {};
  const payloadCorrect = toFiniteNumber(data.problemsAnsweredCorrectly ?? data.correctAnswers);
  const payloadTotal = toFiniteNumber(data.allProblemsCount ?? data.totalQuestions);
  const feedbackCorrect = toFiniteNumber(feedbackStats?.correctAnswers);
  const feedbackTotal = toFiniteNumber(feedbackStats?.totalQuestions);

  const resolvedCorrect = feedbackCorrect !== null && feedbackCorrect > (payloadCorrect ?? -1)
    ? feedbackCorrect
    : (payloadCorrect ?? feedbackCorrect ?? 0);

  const resolvedTotal = payloadTotal !== null && payloadTotal > 0
    ? payloadTotal
    : (feedbackTotal ?? data.allProblemsCount ?? data.totalQuestions ?? 0);

  return {
    ...payload,
    data: {
      ...data,
      problemsAnsweredCorrectly: resolvedCorrect,
      correctAnswers: resolvedCorrect,
      allProblemsCount: resolvedTotal,
      totalQuestions: resolvedTotal,
      problemsAnsweredIncorrectly:
        resolvedTotal !== null && Number.isFinite(Number(resolvedTotal))
          ? Math.max(0, Number(resolvedTotal) - Number(resolvedCorrect || 0))
          : data.problemsAnsweredIncorrectly,
      answersByQuestion: Array.isArray(feedbackStats?.answersByQuestion)
        ? feedbackStats.answersByQuestion
        : data.answersByQuestion,
      rawAssessment: {
        ...(data.rawAssessment || {}),
        answersByQuestion: Array.isArray(feedbackStats?.answersByQuestion)
          ? feedbackStats.answersByQuestion
          : data?.rawAssessment?.answersByQuestion,
      },
    },
  };
};


export default function (state = INITIAL_STATE, action) {
  if (action.filter !== filter) {
    return state;
  }

  switch (action.type) {
    case SET_CURRENT_ACTIVITY:
      const { payload } = action;

      if (!payload) return INITIAL_STATE;

      return {
        ...INITIAL_STATE,
        ...payload,
      };
    case RESTART_ACTIVITY: {
      const newState = {
        ...INITIAL_STATE,
        isPreview: state.isPreview,
        goBackHandler: state.goBackHandler,
        currentActivity: { ...state.currentActivity },
        gameStartedAt: state.gameStartedAt,
        numberOfPlays: state.numberOfPlays,
        idleTime: state.idleTime,
        players: state.players,
        multiplayerModeEnabled: state.multiplayerModeEnabled,
      };

      if (state.multiplayerModeEnabled) {
        Object.assign(
          newState, 
          prepareMultiplayerState(state.players),
        );
      };

      return newState;
    }
    case GAME_INITIALIZED:
      return {
        ...state,
        gameInitialized: true,
        gameStartedAt: state.gameStartedAt || Date.now(),
        numberOfPlays: state.numberOfPlays + 1,
      };
    case OPEN_ACTIVITY_HELP:
      return { ...state, isActivityHelpOpened: true };
    case OPEN_TEAM_ACTIVITY_HELP:
      return { ...state, isTeamActivityHelpOpened: true };
    case SHOW_FEEDBACK:
      return {
        ...state,
        feedback: action.payload,
        assessmentFeedbackStats: updateAssessmentFeedbackStats(
          state.assessmentFeedbackStats,
          action.payload,
        ),
      };
    case CLOSE_FEEDBACK:
      return { ...state, feedback: null };
    case SHOW_STATS:
      return {
        ...state,
        stats: normalizeStatsWithAssessmentFeedback(
          action.payload,
          state.assessmentFeedbackStats,
        ),
      };
    case CLOSE_ACTIVITY_HELP:
      if (!state.initialActivityHelpShown) {
        return {
          ...state,
          isActivityHelpOpened: false,
          initialActivityHelpShown: true,
          shouldGameInit: true,
        };
      } else {
        return {
          ...state,
          isActivityHelpOpened: false,
        };
      }
    case CLOSE_TEAM_ACTIVITY_HELP:
      if (!state.initialTeamActivityHelpShown) {
        return {
          ...state,
          isTeamActivityHelpOpened: false,
          initialTeamActivityHelpShown: true,
          shouldGameInit: true,
        };
      } else {
        return {
          ...state,
          isTeamActivityHelpOpened: false,
        };
      }
    case SHOW_GAME_PARENT_ADVICE:
      return {
        ...state,
        gameParentAdvice: action.payload,
        gameMultiplayerAdvice: false,
      };
    case SHOW_GAME_MULTIPLAYER_ADVICE:
      return {
        ...state,
        gameMultiplayerAdvice: action.payload,
        gameParentAdvice: false,
      };
    case ADD_IDLE_TIME:
      return {
        ...state,
        idleTime: state.idleTime + action.payload,
      };
    default:
      return state;
    case CHANGE_PLAYER_TURN: {
      const { players, turningPlayerId } = state;

      let nextPlayerIndex = players.indexOf(_.find(players, { _id: turningPlayerId })) + 1;
      const maxIndex = players.length - 1;

      if (nextPlayerIndex > maxIndex) {
        nextPlayerIndex = 0;
      }

      return {
        ...state,
        turningPlayerId: players[nextPlayerIndex]._id,
      };
    };
    case INCREMENT_PLAYER_POINTS: {
      const { turningPlayerId, powerLevels } = state;
      if (turningPlayerId === null) {
        console.log('incremented points of a player (no current turn)');
        return state;
      }

      const { numGuesses } = action.payload;
      console.log(`Player has guessed the right answer in ${numGuesses} tries`);

      const pointsMax = 500;
      const pointsPenalty = numGuesses > 0 ?
        (100 * _.clamp(numGuesses, 1, 4)) :
        0;
      
      // ^
      // for each missed guess subtract 100 pts,
      // also clamping to 4 max because player needs to get minimum of 100 points

      const pointMultiplier = powerLevels[turningPlayerId] ?? 1;

      const playerPoints = state.points[turningPlayerId];
      const pointsGain = (pointsMax - pointsPenalty) * pointMultiplier;

      return {
        ...state,
        points: {
          ...state.points,
          [turningPlayerId]: playerPoints + pointsGain,
        },
      };
    };
    case SET_PLAYER_POWER_LEVEL: {
      const { id, powerLevel } = action.payload;
      return {
        ...state,
        powerLevels: {
          ...state.powerLevels,
          [id]: powerLevel,
        },
      };
    };
  }
}
